import { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { isEmpty, omit } from 'lodash'
import { useDispatch, useSelector } from 'react-redux'

import API from 'api'
import { getAppKey } from 'models/application'
import { getAppRoute } from 'helpers/application'
import { getEvents, getBundles, getEventUrl } from 'models/events'
import { isCitifydApp } from 'helpers/application'
import { loadEvents } from 'helpers/events'
import { sendPageSize } from 'helpers/sdk'
import { showModal } from 'components/Atoms/Modal/actions'
import { useTranslation } from 'i18n-web/i18next'

const WithEvents = WrapperComponent => {
  const Wrapper = props => {
    const { application, filterBy, page, router, sdk } = props

    const getInitialCurrentPage = () => {
      if (!Number.isInteger(parseInt(page, 10)) || page < 1) return 1

      return parseInt(page, 10)
    }

    const dispatch = useDispatch()
    const isSDK = useSelector(state => state.settings.isSDK) || sdk
    const disableHeaderAndFooter = useSelector(
      state => state.settings.disableHeaderAndFooter
    )
    const user = useSelector(state => state.user.me)
    const loggedIn = useSelector(state => state.session.loggedIn)
    const parentDomain = useSelector(state => state.settings.parentDomain)
    const { t } = useTranslation()

    const [bundleList, setBundleList] = useState()
    const [currentPage, setCurrentPage] = useState(getInitialCurrentPage())
    const [eventList, setEventList] = useState()
    const [filters, setFilters] = useState()
    const [isLoadingEvents, setIsLoadingEvents] = useState(true)
    const [moreResultsAvailable, setMoreResultsAvailable] = useState()
    const [pageCount, setPageCount] = useState()

    const phoneNumberSupportSDK = useSelector(
      state => state.settings.phoneNumberSupport
    )
    const emailSupportSDK = useSelector(state => state.settings.emailSupport)

    const emailSupport = isSDK
      ? emailSupportSDK
      : process.env.NEXT_PUBLIC_CITIFYD_EMAIL

    const phoneNumberSupport = isSDK
      ? phoneNumberSupportSDK
      : process.env.NEXT_PUBLIC_CITIFYD_PHONE_NUMBER

    const fetchEvents = (page = currentPage) => {
      loadEvents(page, filterBy, isSDK, application)
        .then(resp => {
          const { pathname, query } = router
          const nextQuery = {
            ...omit(query, 'application'),
            page: page,
          }

          const nextPathname = pathname.replace('/[application]', '')

          if (nextPathname === '/events') {
            router.push(
              {
                pathname: `${getAppRoute(application, 'as')}${nextPathname}`,
                query: nextQuery,
              },
              {
                pathname: `${getAppRoute(application, 'url')}${nextPathname}`,
                query: nextQuery,
              }
            )

            setCurrentPage(page)
            setMoreResultsAvailable(resp?.moreResultsAvailable)
            setEventList(getEvents(resp?.events))
            setBundleList(getBundles(resp?.bundles))
            setPageCount(resp?.pageCount)
            setTimeout(() => isSDK && sendPageSize({ parentDomain }), 100)
          }
        })
        .finally(() => {
          setIsLoadingEvents(false)
        })
    }

    useEffect(() => {
      fetchEvents()
    }, [filterBy])

    useEffect(() => {
      const getFilters = async () =>
        await API()
          .getEventsFilters({
            ...(isSDK && { sdk: true }),
            ...(application &&
              !isCitifydApp(application) && {
                headers: { 'App-Key': getAppKey(application) },
              }),
          })
          .then(resp => setFilters(resp?.eventFilters))

      getFilters()
    }, [])

    const onEventSelected = event => {
      const {
        query: { lotId },
      } = router

      const parsedLotId = parseInt(lotId)

      const handleRouterPush = () => {
        if (parsedLotId && loggedIn && !isEmpty(user) && !user?.isGuest) {
          const route = {
            url: `${getAppRoute(
              application,
              'as'
            )}/events/[event]/lots/[lot]/purchase`,
            as: `${getAppRoute(application, 'url')}/events/${getEventUrl(
              event
            )}/lots/${parsedLotId}/purchase`,
          }

          router.push(route.url, route.as)

          return
        }

        if (parsedLotId) {
          router.push(
            `${getAppRoute(
              application,
              'as'
            )}/events/[event]?lotId=${parsedLotId}`,
            `${getAppRoute(application, 'url')}/events/${getEventUrl(event)}`
          )

          return
        }

        router.push(
          `${getAppRoute(application, 'as')}/events/[event]`,
          `${getAppRoute(application, 'url')}/events/${getEventUrl(event)}`
        )
      }

      handleRouterPush()
    }

    const onBundleSelected = bundle => {
      // eslint-disable-next-line prettier/prettier

      const nextRoute = `${getAppRoute(
        application,
        'url'
      )}/events/bundles/${getEventUrl(bundle)}/purchase`

      if (loggedIn && !isEmpty(user) && !user?.isGuest) {
        /* eslint-disable prettier/prettier */
        router.push(
          `${getAppRoute(application, 'as')}/events/bundles/[bundle]/purchase`,
          `${getAppRoute(application, 'url')}/events/bundles/${getEventUrl(
            bundle
          )}/purchase`
        )
        /* eslint-disable prettier/prettier */
      } else {
        dispatch(
          showModal({
            childProps: {
              redirectTo: nextRoute,
            },
            modalType: 'signIn',
          })
        )
      }
    }

    const loadingEvents = {
      status: isLoadingEvents,
      setStatus: status => setIsLoadingEvents(status),
    }

    return (
      <WrapperComponent
        {...props}
        bundleList={bundleList}
        currentPage={currentPage}
        eventFilters={filters}
        eventList={eventList}
        fetchEvents={fetchEvents}
        loadingEvents={loadingEvents}
        moreResultsAvailable={moreResultsAvailable}
        onBundleSelected={bundle => onBundleSelected(bundle)}
        onEventSelected={event => onEventSelected(event)}
        pageCount={pageCount}
        setCurrentPage={setCurrentPage}
        emailSupport={emailSupport}
        phoneNumberSupport={phoneNumberSupport}
        disableHeaderAndFooter={disableHeaderAndFooter}
        isSDK={isSDK}
        t={t}
      />
    )
  }

  if (WrapperComponent.getInitialProps) {
    Wrapper.getInitialProps = WrapperComponent.getInitialProps
  }

  Wrapper.propTypes = {
    appKey: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
    application: PropTypes.string,
    bundles: PropTypes.arrayOf(PropTypes.object),
    events: PropTypes.arrayOf(PropTypes.object),
    router: PropTypes.object,
    filterBy: PropTypes.string,
    page: PropTypes.string,
    sdk: PropTypes.bool,
  }

  return Wrapper
}

export default WithEvents
